import React from 'react'
import PropTypes from 'prop-types'
import Label from './Label'
import Textarea from './Textarea'
import Control from './Control'
import Field from './Field'

/* doc
---
title: TextareaField
name: TextareaField
category: React - Bulma
parent: Field
---

A `TextareaField` is a wrapper for combining `Field` and `Textarea` components.

Use this if you need to add a Textarea field to a form.

See [Bulma Documentation](http://bulma.io/documentation/form/textarea/) for reference and full options.
If one of the Bulma options isn't listed below, it may be added using the `className` prop.

### Props
| Prop Name       | Description                                   |Type            | Default Value             | Required?     |
|-----------------|-----------------------------------------------|----------------|---------------------------|---------------|
| name            | Name/id for select field                      | string         |                           | yes           |
| value           | defaultValue for input                        | any            |                           | no            |
| size            | Size of input                                 | string         |                           | no            |
| label           | Label's display text                          | string         |                           | no            |
| fieldProps      | Props for `Field` component                   | object         | {}                        | no            |
| labelProps      | Props for `Label` component                   | object         | {}                        | no            |
| controlProps    | Props for `Control` component                 | object         | {}                        | no            |
| inputProps      | Props for `Input` component                   | object         | {}                        | no            |


```html_example
  <TextareaField
    name="example"
    value="starting value"
    label="Example Input Field"
  />
```
*/

/**
* TextareaField Component. Used to maintain consistency for html/css regarding
* textarea fields.
*
* @TODO Add support for input colors/states.
*/
function TextareaField(props) {
  const {
    onChange, label, name, value, type, size, fieldProps, labelProps, controlProps, inputProps, ...rest
  } = props

  return (
    <Field {...fieldProps} {...rest}>
      {label ? <Label htmlFor={name} size={size} {...labelProps}>{label}</Label> : false}
      <Control {...controlProps}>
        <Textarea
          inputSize={size}
          {...inputProps}
          onChange={onChange}
          name={name}
          value={value}
        />
      </Control>
    </Field>
  )
}

TextareaField.defaultProps = {
  fieldProps: {},
  labelProps: {},
  controlProps: {},
  inputProps: {},
}

TextareaField.propTypes = {
  // Input name/id
  name: PropTypes.string.isRequired,
  // Default value for input.
  value: PropTypes.any,
  // Label display text.
  label: PropTypes.string,
  // Props for `Field` component.
  fieldProps: PropTypes.object,
  // Props for `Label` component.
  labelProps: PropTypes.object,
  // Props for `Control` component.
  controlProps: PropTypes.object,
  // Props for `Input` component.
  inputProps: PropTypes.object,
}

export default TextareaField
